import React, { useCallback, useState, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Set } from 'immutable';
import { useConfirm } from 'material-ui-confirm';

import toast from '@/components/Toast';
import TagBox from '@/pages/common/Dialog/DialogTagsManage/components/TagBox';
import Dialog from '@/components/mui/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TagSuggestionsAutocomplete from '@/components/mui/Layouts/components/TagSuggestionsAutocomplete';
import TagsSearchInput from '@/pages/common/Dialog/DialogTagsManage/components/TagsSearchInput';

import { documentMultipleUpdateTags } from '@/domain/documents';
import { chatAllUsersByIdSelector } from '@/domain/chat/chatSelector';
import { ROLES_FOR_CONFIRM } from '@/pages/common/Dialog/DialogTagsManage';

const getInitialTags = (documents) => {
  const documentsTags = documents.map((doc) => doc.tags.filter((tag) => tag[0] !== '_'));
  const allTags = Set.union(documentsTags);
  const commonTags = allTags.filter((tag) => documentsTags.every((docTags) => docTags.includes(tag)));
  const uniqueTags = allTags.filter((tag) => !commonTags.includes(tag));
  return { commonTags, uniqueTags, allTags };
};

const TagMultiple = ({ documents, onCancel, open }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const { formatMessage } = useIntl();
  const [value, setValue] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const closeAfterSuccessRef = useRef(null);

  const users = useSelector(chatAllUsersByIdSelector);

  const checkUserForConfirm = useCallback(
    (tag) => {
      const user = users.get(tag);

      return user && ROLES_FOR_CONFIRM.includes(user.role) && user.restricted;
    },
    [users],
  );

  const sortedTags = useMemo(() => {
    const { commonTags, uniqueTags } = getInitialTags(documents);

    const isUser = (tag) => +!!users.find((user) => user.userId === tag);
    const common = commonTags.sort((a, b) => isUser(b) - isUser(a));
    const unique = uniqueTags.sort((a, b) => isUser(b) - isUser(a));
    return { commonTags: common, uniqueTags: unique };
  }, [users, documents]);

  const onSubmit = useCallback(
    (payload) => {
      setIsBusy(true);

      new Promise((resolve, reject) => {
        dispatch(
          documentMultipleUpdateTags({
            resolve,
            reject,
            documentID: documents.map((doc) => doc.documentID),
            ...payload,
          }),
        );
      })
        .then(({ success, total }) => {
          toast.success(
            formatMessage(
              {
                id: 'documents.bulkActions.addTags',
                defaultMessage: '{success} out of {total} documents were modified',
              },
              { success, total },
            ),
          );

          if (closeAfterSuccessRef.current) {
            closeAfterSuccessRef.current = false;
            onCancel();
          }
        })
        .catch(() => {
          toast.error(formatMessage({ id: 'documents.bulkActions.addTagsFailure', defaultMessage: 'Failed' }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    },
    [formatMessage, dispatch, documents, onCancel],
  );

  const handleSubmit = useCallback(
    (tag, isRemove = false) => {
      const tagIsUser = checkUserForConfirm(tag);

      const payload = isRemove ? { tagsToRemove: [tag], tagsToAdd: [] } : { tagsToRemove: [], tagsToAdd: [tag] };

      if (!tagIsUser) {
        onSubmit(payload);
      } else {
        confirm({
          title: formatMessage({
            id: 'confirm.userGainingDocumentAccess.title',
            defaultMessage: 'Access to document',
          }),
          description: (
            <>
              {!isRemove && (
                <Box mb={2}>
                  {formatMessage(
                    {
                      id: 'confirm.userAllowDocumentAccess.placeholder',
                      defaultMessage: `This action will enable ${tag} to view the document`,
                    },
                    {
                      usernames: <strong>{tag}</strong>,
                    },
                  )}
                </Box>
              )}
              {isRemove && (
                <Box>
                  {formatMessage(
                    {
                      id: 'confirm.userDenyDocumentAccess.placeholder',
                      defaultMessage: `This action will disable ${tag} to view the document`,
                    },
                    {
                      usernames: <strong>{tag}</strong>,
                    },
                  )}
                </Box>
              )}
            </>
          ),
          confirmationButtonProps: {
            color: isRemove ? 'error' : 'primary',
          },
        }).then(() => {
          onSubmit(payload);
        });
      }
    },
    [onSubmit, confirm, formatMessage, checkUserForConfirm],
  );

  const onDeleteTag = useCallback(
    (tag) => {
      handleSubmit(tag, true);
    },
    [handleSubmit],
  );

  const handleInputChange = useCallback((v) => {
    setValue(v);
  }, []);

  const isAddForbidden = useCallback(
    (tag) => {
      const currentTag = tag || value;

      return (
        sortedTags.commonTags.includes(currentTag) ||
        !currentTag ||
        (typeof currentTag === 'string' && currentTag[0] === '_')
      );
    },
    [sortedTags.commonTags, value],
  );

  const handleTagSelect = useCallback(
    (tag) => {
      if (!isAddForbidden(tag)) {
        handleSubmit(tag);
        setValue('');
      }
    },
    [handleSubmit, isAddForbidden],
  );

  const handleOK = useCallback(() => {
    if (isAddForbidden(value)) {
      onCancel();
    } else {
      handleTagSelect(value);
      closeAfterSuccessRef.current = true;
    }
  }, [handleTagSelect, value, onCancel, isAddForbidden]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={formatMessage({ id: 'modal.document.tags.title', defaultMessage: 'Document Tags' })}
      maxWidth="sm"
      withActions={false}
      withContent={false}
      slotProps={{
        paper: { sx: { overflow: 'inherit' } },
      }}
    >
      <DialogContent>
        <TagSuggestionsAutocomplete
          inputValue={value}
          onInputChange={handleInputChange}
          onSelect={handleTagSelect}
          prefixOutputWithHashtag={false}
          renderInput={(params) => (
            <TagsSearchInput
              params={params}
              disabled={isAddForbidden() || isBusy}
              isBusy={isBusy}
              onAdd={() => handleTagSelect(value)}
            />
          )}
        />
        <Stack direction="row" flexWrap="wrap" gap={0.5} mt={2} mb={1}>
          {sortedTags.commonTags.map((tag) => (
            <TagBox key={tag} tag={tag} onDelete={onDeleteTag} disabled={isBusy} />
          ))}
        </Stack>
        {sortedTags.uniqueTags.size > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {sortedTags.uniqueTags.map((tag) => (
              <TagBox key={tag} tag={tag} onDelete={onDeleteTag} disabled={isBusy} isUnique />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="text">
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button onClick={handleOK} sx={{ minWidth: '10ch' }}>
          {formatMessage({ id: 'button.ok', defaultMessage: 'OK' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagMultiple;
